import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { ExternalLink, Package, DollarSign, Tag, Barcode, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { getCurrencySymbol } from "@/lib/currency";

interface ProductPreviewProps {
  product: {
    id?: string;
    woo_product_id?: number;
    name: string;
    regular_price?: number | string;
    price?: number | string;
    description?: string;
    short_description?: string;
    sku?: string;
    status?: string;
    seo_score?: number;
    permalink?: string;
  };
  action: "created" | "updated" | "listed";
  storeUrl?: string;
  currency?: string;
}

export function ProductPreview({ product, action, storeUrl, currency = "USD" }: ProductPreviewProps) {
  const currencySymbol = getCurrencySymbol(currency);
  const price = product.regular_price || product.price;
  const seoScore = product.seo_score || 0;
  
  const actionConfig = {
    created: { label: "Created", className: "bg-green-500/10 text-green-600 border-green-500/20" },
    updated: { label: "Updated", className: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
    listed: { label: "Found", className: "bg-muted text-muted-foreground border-border" },
  };

  const seoScoreColor = seoScore >= 70 
    ? "text-green-600" 
    : seoScore >= 40 
      ? "text-yellow-600" 
      : "text-red-600";

  return (
    <Card className="mt-3 p-3 bg-background/50 border-border/50">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center">
            <Package className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-sm truncate">{product.name}</h4>
              <Badge 
                variant="outline" 
                className={cn("text-[10px] px-1.5 py-0", actionConfig[action].className)}
              >
                {actionConfig[action].label}
              </Badge>
            </div>
            {product.woo_product_id && (
              <p className="text-[10px] text-muted-foreground">
                WooCommerce ID: #{product.woo_product_id}
              </p>
            )}
          </div>
        </div>
        
        {product.permalink && (
          <a 
            href={product.permalink} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        {price && (
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <DollarSign className="h-3 w-3" />
            <span className="font-medium text-foreground">
              {currencySymbol}{typeof price === 'string' ? price : price.toFixed(2)}
            </span>
          </div>
        )}
        
        {product.sku && (
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Barcode className="h-3 w-3" />
            <span className="truncate">{product.sku}</span>
          </div>
        )}
        
        {product.status && (
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Tag className="h-3 w-3" />
            <span className="capitalize">{product.status}</span>
          </div>
        )}
        
        {seoScore > 0 && (
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <TrendingUp className="h-3 w-3" />
            <span className={cn("font-medium", seoScoreColor)}>
              SEO: {seoScore}/100
            </span>
          </div>
        )}
      </div>

      {(product.description || product.short_description) && (
        <p className="mt-2 text-xs text-muted-foreground line-clamp-2">
          {product.short_description || product.description}
        </p>
      )}
    </Card>
  );
}
